import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import TurndownService from 'turndown';
import { motion, AnimatePresence } from 'motion/react';
import { AudioRecorder } from '~/components/shared/AudioRecorder';
import {
  XMarkIcon,
  DocumentTextIcon,
  LanguageIcon,
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  ListBulletIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/solid';
import 'react-quill/dist/quill.snow.css';

type Language = 'en' | 'es';

interface LanguageOption {
  code: Language;
  label: string;
}

interface PromptData {
  [key: string]: string;
  en: string;
  es: string;
}

interface PromptEditorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  promptData: PromptData;
  onSave: (promptData: PromptData) => void;
  agentId: string;
  defaultLanguage: Language;
  currentLanguage: Language;
  onLanguageChange: (language: Language) => void;
}

// Initialize Turndown service for HTML to Markdown conversion
const turndownService = new TurndownService({
  headingStyle: 'atx',
  bulletListMarker: '-',
  codeBlockStyle: 'fenced',
  fence: '```',
  emDelimiter: '*',
  strongDelimiter: '**',
  linkStyle: 'inlined',
  linkReferenceStyle: 'full',
});

// Configure turndown to handle Quill-specific HTML
turndownService.addRule('lineBreak', {
  filter: 'br',
  replacement: () => '\n',
});

turndownService.addRule('paragraph', {
  filter: 'p',
  replacement: (content) => (content ? content + '\n\n' : '\n'),
});

// Utility functions for format conversion
const markdownToHtml = (markdown: string): string => {
  if (!markdown) return '';

  // Basic markdown to HTML conversion with better whitespace handling
  return markdown
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/__(.*?)__/g, '<u>$1</u>')
    .replace(/^# (.*$)/gim, '<h1>$1</h1>')
    .replace(/^## (.*$)/gim, '<h2>$1</h2>')
    .replace(/^### (.*$)/gim, '<h3>$1</h3>')
    .replace(/^- (.*$)/gim, '<ul><li>$1</li></ul>')
    .replace(/^\d+\. (.*$)/gim, '<ol><li>$1</li></ol>')
    .replace(/<\/ul>\s*<ul>/g, '')
    .replace(/<\/ol>\s*<ol>/g, '')
    .split('\n\n')
    .map((paragraph) => (paragraph.trim() ? `<p>${paragraph}</p>` : ''))
    .join('');
};

const htmlToMarkdown = (html: string): string => {
  if (!html) return '';

  // Use turndown to convert HTML to markdown
  let markdown = turndownService.turndown(html);

  // Clean up extra whitespace and normalize line breaks
  markdown = markdown
    .replace(/\n{3,}/g, '\n\n') // Max 2 consecutive newlines
    .replace(/[ \t]+$/gm, '') // Remove trailing spaces
    .replace(/^[ \t]+/gm, '') // Remove leading spaces (except for code blocks)
    .trim();

  return markdown;
};

export const PromptEditorDialog = ({
  isOpen,
  onClose,
  promptData,
  onSave,
  agentId,
  defaultLanguage,
  currentLanguage,
  onLanguageChange,
}: PromptEditorDialogProps) => {
  const [t] = useTranslation('training_settings');
  const [content, setContent] = useState('');
  const [localPromptData, setLocalPromptData] =
    useState<PromptData>(promptData);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const quillRef = useRef<ReactQuill>(null);

  // Available languages
  const languages: LanguageOption[] = [
    { code: 'en', label: t('english') },
    { code: 'es', label: t('spanish') },
  ];

  // Simple content change handler - no conversion during typing
  const handleContentChange = useCallback((value: string) => {
    setContent(value);
  }, []);

  // Stable key for ReactQuill
  const quillKey = `prompt-quill-${currentLanguage}-${isOpen}`;

  // Enhanced Quill modules configuration
  const stableModules = useMemo(
    () => ({
      toolbar: false,
      keyboard: {
        bindings: {
          tab: {
            key: 9,
            handler: function () {
              return true;
            },
          },
        },
      },
    }),
    [],
  );

  // Stable formats array
  const stableFormats = useMemo(
    () => [
      'header',
      'font',
      'size',
      'bold',
      'italic',
      'underline',
      'strike',
      'blockquote',
      'list',
      'bullet',
      'indent',
      'link',
      'color',
      'background',
      'align',
    ],
    [],
  );

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      setContent('');
      setLocalPromptData(promptData);
      setIsSaving(false);
      setSelectedText('');
      setCursorPosition(0);
      setIsSidebarOpen(false);
    } else {
      // Initialize with current language content when opening
      const currentMarkdown = promptData[currentLanguage] || '';
      const htmlContent = markdownToHtml(currentMarkdown);
      setContent(htmlContent);
      setLocalPromptData(promptData);
    }
  }, [isOpen, promptData, currentLanguage]);

  // Load prompt content when language changes
  useEffect(() => {
    if (isOpen && localPromptData[currentLanguage] !== undefined) {
      const currentMarkdown = localPromptData[currentLanguage] || '';
      const htmlContent = markdownToHtml(currentMarkdown);
      setContent(htmlContent);
    }
  }, [currentLanguage, isOpen]); // Removed localPromptData dependency to avoid loops

  // Handle cursor position and selection changes
  const handleSelectionChange = useCallback((range: any) => {
    if (range) {
      setCursorPosition(range.index);
      if (range.length > 0) {
        const quill = quillRef.current?.getEditor();
        if (quill) {
          const selectedContent = quill.getText(range.index, range.length);
          setSelectedText(selectedContent.trim());
        }
      } else {
        setSelectedText('');
      }
    }
  }, []);

  // Audio transcription handler
  const handleAudioTranscription = useCallback(
    (transcription: string) => {
      const quill = quillRef.current?.getEditor();
      if (quill) {
        quill.insertText(cursorPosition, transcription + ' ');
        quill.setSelection(cursorPosition + transcription.length + 1, 0);
      }
    },
    [cursorPosition],
  );

  const handleLanguageSwitch = (language: Language) => {
    // Convert current HTML content to markdown and save before switching
    const currentMarkdown = htmlToMarkdown(content);

    setLocalPromptData((prev) => ({
      ...prev,
      [currentLanguage]: currentMarkdown,
    }));

    onLanguageChange(language);
  };

  const handleSave = () => {
    setIsSaving(true);

    // Convert final HTML content to markdown for storage
    const finalMarkdownContent = htmlToMarkdown(content);

    // Final prompt data with all languages' content as markdown
    const finalPromptData = {
      ...localPromptData,
      [currentLanguage]: finalMarkdownContent,
    };

    onSave(finalPromptData);
    onClose();
  };

  const toggleFormat = (format: string) => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const currentFormat = quill.getFormat();
      quill.format(format, !currentFormat[format]);
    }
  };

  const insertList = (type: 'ordered' | 'bullet') => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      quill.format('list', type);
    }
  };

  // Sidebar animation variants
  const sidebarVariants = {
    closed: {
      width: 0,
      opacity: 0,
      transition: {
        duration: 0.3,
        ease: 'easeInOut',
      },
    },
    open: {
      width: 320,
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: 'easeInOut',
      },
    },
  };

  if (!isOpen) {
    return null;
  }

  const currentLanguageLabel =
    languages.find((lang) => lang.code === currentLanguage)?.label || '';
  const defaultLanguageLabel =
    languages.find((lang) => lang.code === defaultLanguage)?.label || '';

  // Get character count from current HTML content (converted to text for accurate count)
  const textContent = content
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  const characterCount = textContent.length;
  const wordCount = textContent
    .split(/\s+/)
    .filter((word) => word.length > 0).length;

  return (
    <div className="bg-base-100 fixed inset-0 z-50">
      {/* Custom styles for prompt editing */}
      <style>{`
        .ql-editor {
          color: #1f2937 !important;
          font-size: 16px !important;
          line-height: 1.6 !important;
          background: white !important;
          min-height: 400px !important;
          padding: 24px !important;
        }
        .ql-editor p {
          color: #1f2937 !important;
          margin: 0 0 16px 0 !important;
          font-size: 16px !important;
        }
        .ql-editor * {
          color: #1f2937 !important;
        }
        .ql-container {
          border: none !important;
          font-size: 16px !important;
          background: white !important;
        }
        .ql-toolbar {
          display: none !important;
        }
        .ql-blank::before {
          color: #9ca3af !important;
          font-style: italic !important;
          font-size: 16px !important;
        }
      `}</style>

      {/* Header */}
      <div className="border-base-300 bg-base-100 flex h-16 items-center justify-between border-b px-4 shadow-sm">
        <div className="flex items-center space-x-4">
          <button
            onClick={onClose}
            className="btn btn-ghost btn-sm"
            aria-label="Close prompt editor"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
          <h1 className="text-lg font-semibold">
            <DocumentTextIcon className="mr-2 inline h-5 w-5" />
            {t('prompt_editor', 'Agent Prompt Editor')}
          </h1>
          <div className="badge badge-info badge-sm text-gray-900">
            {t('markdown_format', 'Markdown Format')}
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {/* Language Indicator */}
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <LanguageIcon className="h-4 w-4" />
            <span>{currentLanguageLabel}</span>
            {defaultLanguage === currentLanguage && (
              <span className="badge badge-primary badge-sm text-white">
                {t('default', 'Default')}
              </span>
            )}
          </div>

          {/* Audio Recorder */}
          <div className="tooltip tooltip-bottom" data-tip="Voice input">
            <AudioRecorder
              onTranscriptionComplete={handleAudioTranscription}
              enablePulseAnimation={false}
            />
          </div>

          {/* Save Button */}
          <button
            onClick={handleSave}
            className="btn btn-primary btn-sm rounded-full"
            disabled={isSaving || !textContent.trim()}
          >
            {isSaving ? (
              <span className="loading loading-spinner loading-sm"></span>
            ) : (
              t('save', 'Save')
            )}
          </button>
        </div>
      </div>

      {/* Custom Toolbar */}
      <div className="bg-base-50 border-base-300 flex flex-wrap items-center gap-2 border-b p-2">
        {/* Text Formatting */}
        <div className="join">
          <button
            className="btn btn-sm join-item"
            onClick={() => toggleFormat('bold')}
            title="Bold (Markdown: **text**)"
          >
            <BoldIcon className="h-4 w-4" />
          </button>
          <button
            className="btn btn-sm join-item"
            onClick={() => toggleFormat('italic')}
            title="Italic (Markdown: *text*)"
          >
            <ItalicIcon className="h-4 w-4" />
          </button>
          <button
            className="btn btn-sm join-item"
            onClick={() => toggleFormat('underline')}
            title="Underline"
          >
            <UnderlineIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="divider divider-horizontal"></div>

        {/* Headers */}
        <select
          className="ql-header select select-sm"
          title="Headers (Markdown: # ## ###)"
        >
          <option value="">Normal</option>
          <option value="1">Heading 1</option>
          <option value="2">Heading 2</option>
          <option value="3">Heading 3</option>
        </select>

        <div className="divider divider-horizontal"></div>

        {/* Lists */}
        <div className="join">
          <button
            className="btn btn-sm join-item"
            onClick={() => insertList('bullet')}
            title="Bullet List (Markdown: - item)"
          >
            <ListBulletIcon className="h-4 w-4" />
          </button>
          <button
            className="ql-list btn btn-sm join-item"
            value="ordered"
            title="Numbered List (Markdown: 1. item)"
          >
            1.
          </button>
        </div>

        <div className="divider divider-horizontal"></div>

        {/* Sidebar Toggle */}
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className={`btn btn-sm ${
            isSidebarOpen ? 'btn-primary' : 'btn-ghost'
          }`}
          title="Toggle Language Panel"
        >
          <Cog6ToothIcon className="h-4 w-4" />
          {isSidebarOpen
            ? t('hide_languages', 'Hide Languages')
            : t('show_languages', 'Show Languages')}
        </button>
      </div>

      {/* Main Content Area */}
      <div className="flex h-[calc(100vh-8rem)] overflow-hidden">
        {/* Editor */}
        <div className="flex flex-1 flex-col bg-gray-100">
          <div className="relative flex-1 overflow-auto">
            <div className="mx-auto max-w-4xl px-8 py-6">
              <div className="min-h-[400px] rounded-lg bg-white p-6 shadow-sm">
                <ReactQuill
                  ref={quillRef}
                  key={quillKey}
                  theme="snow"
                  value={content}
                  onChange={handleContentChange}
                  onChangeSelection={handleSelectionChange}
                  modules={stableModules}
                  formats={stableFormats}
                  className="min-h-[400px]"
                  placeholder={t(
                    'prompt_placeholder',
                    'Enter the instructions for your agent...',
                  )}
                  style={{
                    height: '400px',
                    minHeight: '400px',
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Animated Sidebar */}
        <AnimatePresence>
          {isSidebarOpen && (
            <motion.div
              variants={sidebarVariants}
              initial="closed"
              animate="open"
              exit="closed"
              className="bg-base-50 border-base-300 overflow-hidden border-l"
            >
              <div className="w-80 overflow-y-auto">
                <div className="space-y-6 p-4">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <h3 className="text-base-content flex items-center text-sm font-medium">
                      <LanguageIcon className="mr-2 h-4 w-4" />
                      {t('language_settings', 'Language Settings')}
                    </h3>
                    <button
                      onClick={() => setIsSidebarOpen(false)}
                      className="btn btn-ghost btn-xs"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Language Selector */}
                  <div>
                    <label className="text-base-content mb-2 block text-sm font-medium">
                      {t('current_language', 'Current Language')}
                    </label>
                    <div className="space-y-2">
                      {languages.map((language) => (
                        <button
                          key={language.code}
                          onClick={() => handleLanguageSwitch(language.code)}
                          className={`w-full rounded-lg border px-4 py-3 text-left transition-colors ${
                            currentLanguage === language.code
                              ? 'bg-primary/10 border-primary text-primary'
                              : 'border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium">
                              {language.label}
                            </span>
                            <div className="flex items-center space-x-2">
                              {defaultLanguage === language.code && (
                                <span className="badge badge-primary badge-sm text-white">
                                  {t('default', 'Default')}
                                </span>
                              )}
                              {localPromptData[language.code]?.trim() && (
                                <span className="text-xs text-green-600">
                                  ✓ {t('has_content', 'Has content')}
                                </span>
                              )}
                            </div>
                          </div>
                          {localPromptData[language.code]?.trim() && (
                            <div className="mt-1 line-clamp-2 text-xs text-gray-500">
                              {localPromptData[language.code].substring(0, 100)}
                              ...
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Markdown Info */}
                  <div className="space-y-2">
                    <h3 className="text-base-content text-sm font-medium">
                      {t('markdown_guide', 'Markdown Guide')}
                    </h3>
                    <div className="text-base-content/70 space-y-1 text-xs">
                      <p>
                        <code>**bold**</code> → <strong>bold</strong>
                      </p>
                      <p>
                        <code>*italic*</code> → <em>italic</em>
                      </p>
                      <p>
                        <code># Heading 1</code>
                      </p>
                      <p>
                        <code>## Heading 2</code>
                      </p>
                      <p>
                        <code>- Bullet list</code>
                      </p>
                      <p>
                        <code>1. Numbered list</code>
                      </p>
                    </div>
                  </div>

                  {/* Prompt Guidelines */}
                  <div className="space-y-2">
                    <h3 className="text-base-content text-sm font-medium">
                      {t('prompt_guidelines', 'Prompt Guidelines')}
                    </h3>
                    <div className="text-base-content/70 space-y-1 text-xs">
                      <p>
                        •{' '}
                        {t(
                          'prompt_guideline_1',
                          "Be specific about the agent's role and behavior",
                        )}
                      </p>
                      <p>
                        •{' '}
                        {t(
                          'prompt_guideline_2',
                          'Include examples of desired responses',
                        )}
                      </p>
                      <p>
                        •{' '}
                        {t(
                          'prompt_guideline_3',
                          'Set clear boundaries and limitations',
                        )}
                      </p>
                      <p>
                        •{' '}
                        {t(
                          'prompt_guideline_4',
                          'Use consistent tone and style',
                        )}
                      </p>
                      <p>
                        •{' '}
                        {t(
                          'prompt_guideline_5',
                          'Test with different scenarios',
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Character Count */}
                  <div className="space-y-2">
                    <h3 className="text-base-content text-sm font-medium">
                      {t('content_info', 'Content Info')}
                    </h3>
                    <div className="text-xs text-gray-600">
                      <p>
                        {t('characters', 'Characters')}: {characterCount}
                      </p>
                      <p>
                        {t('words', 'Words')}: {wordCount}
                      </p>
                      <p
                        className={
                          characterCount < 120
                            ? 'text-orange-600'
                            : characterCount > 30000
                              ? 'text-red-600'
                              : 'text-green-600'
                        }
                      >
                        {characterCount < 120
                          ? t('too_short', 'Too short (min 120)')
                          : characterCount > 30000
                            ? t('too_long', 'Too long (max 30000)')
                            : t('length_ok', 'Length OK')}
                      </p>
                      <p className="mt-1 text-xs text-blue-600">
                        {t('format_info', 'Stored as: Markdown')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PromptEditorDialog;
